import {createReducer, on} from '@ngrx/store';
import {INITIAL_LOAD_STATUS_SUBSTATE, LoadStatusSubstate} from '@aviary/store/store.types';
import {Patron, PatronAccountStatus, PatronNotificationPreferences, PatronReferenceOptions, PatronRelationships, RelatedPatron} from './patron.types';
import {PatronActions} from './patron.actions';
import {
  FALLBACK_PATRON,
  FALLBACK_PATRON_ACCOUNT_STATUS,
  FALLBACK_PATRON_NOTIFICATION_PREFERENCES,
  FALLBACK_PATRON_REFERENCE_OPTIONS,
  FALLBACK_PATRON_RELATIONSHIPS,
  FALLBACK_RELATED_PATRON
} from './patron.fallback';
import {FAILED, LOADED, LOADING, RELOADING} from '@aviary/store/common/common.types';

export interface PatronState {
  auth: LoadStatusSubstate & {
    data?: Patron
  };
  patronAccountStatus: LoadStatusSubstate & {
    data?: PatronAccountStatus
  };
  referenceOptions: LoadStatusSubstate & {
    data: PatronReferenceOptions
  };
  notificationPreferences: LoadStatusSubstate & {
    data?: PatronNotificationPreferences
  };
  patronRelationships: LoadStatusSubstate & {
    data?: PatronRelationships
  };
  dependentDetails: LoadStatusSubstate & {
    data?: RelatedPatron
  };
  dependentAccountStatus: LoadStatusSubstate & {
    data?: PatronAccountStatus
  };
}

export const INITIAL_PATRONS_STATE: PatronState = {
  auth: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: FALLBACK_PATRON
  },
  patronAccountStatus: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: FALLBACK_PATRON_ACCOUNT_STATUS
  },
  referenceOptions: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: FALLBACK_PATRON_REFERENCE_OPTIONS
  },
  notificationPreferences: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: FALLBACK_PATRON_NOTIFICATION_PREFERENCES
  },
  patronRelationships: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: FALLBACK_PATRON_RELATIONSHIPS
  },
  dependentDetails: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: FALLBACK_RELATED_PATRON
  },
  dependentAccountStatus: {
    ...INITIAL_LOAD_STATUS_SUBSTATE,
    data: FALLBACK_PATRON_ACCOUNT_STATUS
  }
};

export const patronState = createReducer(
  INITIAL_PATRONS_STATE,
  on(PatronActions.initializeAuthTokenSuccess, state => ({
    ...state,
    auth: {
      ...INITIAL_LOAD_STATUS_SUBSTATE,
      loadStatus: LOADING
    }
  })),
  on(PatronActions.reloadAuthPatron, state => ({
    ...state,
    auth: {
      ...state.auth,
      loadStatus: RELOADING
    }
  })),
  on(
    PatronActions.loadAuthPatronSuccess,
    PatronActions.editPatronSuccess,
    (state, {patron}) => ({
      ...state,
      auth: {
        loadStatus: LOADED,
        data: patron
      }
    })),
  on(PatronActions.initializeAuthTokenNoAuth, (state) => ({
    ...state,
    auth: {
      loadStatus: LOADED
    }
  })),
  on(PatronActions.loadAuthPatronFailure, state => ({
    ...state,
    auth: {
      ...INITIAL_LOAD_STATUS_SUBSTATE,
      loadStatus: FAILED
    }
  })),

  on(PatronActions.loadAccountStatus, state => ({
    ...state,
    patronAccountStatus: {
      ...INITIAL_LOAD_STATUS_SUBSTATE,
      loadStatus: LOADING
    }
  })),
  on(PatronActions.loadAccountStatusSuccess, (state, {status}) => ({
    ...state,
    patronAccountStatus: {
      loadStatus: LOADED,
      data: status
    }
  })),
  on(PatronActions.loadAccountStatusFailure, state => ({
    ...state,
    patronAccountStatus: {
      ...INITIAL_LOAD_STATUS_SUBSTATE,
      loadStatus: FAILED
    }
  })),
  on(
    PatronActions.loadPatronReferenceOptions,
    state => ({
      ...state,
      referenceOptions: {
        loadStatus: LOADING,
        data: FALLBACK_PATRON_REFERENCE_OPTIONS
      }
    })),
  on(
    PatronActions.loadPatronReferenceOptionsSuccess,
    (state, {options}) => ({
      ...state,
      referenceOptions: {
        loadStatus: LOADED,
        data: options
      }
    })
  ),
  on(
    PatronActions.loadPatronReferenceOptionsFailure,
    state => ({
      ...state,
      referenceOptions: {
        loadStatus: FAILED,
        data: FALLBACK_PATRON_REFERENCE_OPTIONS
      }
    })
  ),

  on(
    PatronActions.loadNotificationPreferences,
    PatronActions.editNotificationPreferences,
    state => ({
      ...state,
      notificationPreferences: {
        ...INITIAL_LOAD_STATUS_SUBSTATE,
        loadStatus: LOADING
      }
    })),
  on(
    PatronActions.loadNotificationPreferencesSuccess,
    PatronActions.editNotificationPreferencesSuccess,
    (state, {preferences}) => ({
      ...state,
      notificationPreferences: {
        loadStatus: LOADED,
        data: preferences
      }
    })),
  on(
    PatronActions.loadNotificationPreferencesFailure,
    PatronActions.editNotificationPreferencesFailure,
    state => ({
      ...state,
      notificationPreferences: {
        ...INITIAL_LOAD_STATUS_SUBSTATE,
        loadStatus: FAILED
      }
    })),
  on(
    PatronActions.loadPatronRelationships,
    state => ({
      ...state,
      patronRelationships: {
        ...INITIAL_LOAD_STATUS_SUBSTATE,
        loadStatus: LOADING
      }
    })),
  on(
    PatronActions.loadPatronRelationshipsSuccess,
    (state, {patronRelationships}) => ({
      ...state,
      patronRelationships: {
        loadStatus: LOADED,
        data: patronRelationships
      }
    })),
  on(
    PatronActions.loadPatronRelationshipsFailure,
    state => ({
      ...state,
      patronRelationships: {
        ...INITIAL_LOAD_STATUS_SUBSTATE,
        loadStatus: FAILED
      }
    })),
  on(
    PatronActions.loadDependentDetails,
    state => ({
      ...state,
      dependentDetails: {
        loadStatus: LOADING,
        data: FALLBACK_RELATED_PATRON
      }
    })),
  on(
    PatronActions.loadDependentDetailsSuccess,
    (state, {dependent}) => ({
      ...state,
      dependentDetails: {
        loadStatus: LOADED,
        data: dependent
      }
    })
  ),
  on(
    PatronActions.loadDependentDetailsFailure,
    state => ({
      ...state,
      dependentDetails: {
        loadStatus: FAILED,
        data: FALLBACK_RELATED_PATRON
      }
    })
  ),

  on(PatronActions.loadDependentAccountStatus, state => ({
    ...state,
    dependentAccountStatus: {
      ...INITIAL_LOAD_STATUS_SUBSTATE,
      loadStatus: LOADING
    }
  })),
  on(PatronActions.loadDependentAccountStatusSuccess, (state, {status}) => ({
    ...state,
    dependentAccountStatus: {
      loadStatus: LOADED,
      data: status
    }
  })),
  on(PatronActions.loadDependentAccountStatusFailure, state => ({
    ...state,
    dependentAccountStatus: {
      ...INITIAL_LOAD_STATUS_SUBSTATE,
      loadStatus: FAILED
    }
  })),
);
